import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import { ref } from '@vue/composition-api'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useAuditLog from '@/composables/useAuditLog'

export default function useResidualRiskEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { updateItem, getItem } = useLambda()
  const { addAuditEntry } = useAuditLog()

  const userData = store.state?.userStore?.userData
  const userId = userData?.worker_id?.$oid
  const isSubmitting = ref(false)
  const collection = 'residualAnalysis'
  const arrData = ref({})
  const originalARRData = ref(null) // Store original data for comparison
  const arrId = route.value.params.id
  const query = { _id: oid(arrId), client_id: oid(userData.client.$oid) }

  const show = async () => {
    try {
      isSubmitting.value = true

      const result = await getItem({ collection, query })

      // Inicializar history si no existe (documentos legacy)
      if (result && !result.history) {
        result.history = []
      }

      arrData.value = result || {}
      // Store original data for change detection
      originalARRData.value = JSON.parse(JSON.stringify(result || {}))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.arr_fetch_error') || 'Error al cargar ARR')
    } finally {
      isSubmitting.value = false
    }
  }

  // Build payload helper
  const buildPayload = (data, status = null) => {
    const payload = {
      treatment_id: data.treatment_id ? oid(data.treatment_id) : null,
      inherentAnalysis_id: data.inherentAnalysis_id ? oid(data.inherentAnalysis_id) : null,
      // Inherited from ARI (snapshot)
      inheritedP: data.inheritedP || null,
      inheritedI: data.inheritedI || null,
      inheritedRisk: data.inheritedRisk || null,
      inheritedLevel: data.inheritedLevel || '',
      // Controls
      preventiveControls: data.preventiveControls || '',
      preventiveEffectiveness: data.preventiveEffectiveness || 1,
      correctiveControls: data.correctiveControls || '',
      correctiveEffectiveness: data.correctiveEffectiveness || 1,
      // Calculated residual
      residualP: data.residualP || null,
      residualI: data.residualI || null,
      residualRisk: data.residualRisk || null,
      residualLevel: data.residualLevel || '',
      riskReductionPct: data.riskReductionPct || 0,
      // Action plan
      decision: data.decision || null,
      actionPlan: data.actionPlan || '',
      responsible_id: data.responsible_id ? oid(data.responsible_id) : null,
      targetDate: data.targetDate ? date(data.targetDate) : null,
      status: data.status || 'Pendiente',
    }

    // Only update status if explicitly provided
    if (status !== null) {
      payload.status = status
    }

    return payload
  }

  // Save as draft (for autosave and step navigation)
  // NO registra en audit log porque es autosave automático, no acción del usuario
  // IMPORTANTE: NO actualizar originalData aquí para que detecte cambios al hacer submit final
  const saveDraft = async (data) => {
    try {
      const payload = buildPayload(data)
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // NO actualizar originalData aquí - queremos comparar contra el estado cuando se cargó la página
    } catch (error) {
      console.error('Error saving draft:', error)
      throw error
    }
  }

  // Final update
  const update = async (data) => {
    if (!hasPermission('privacy-residual-risk', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      isSubmitting.value = true

      const payload = buildPayload(data)
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // Add audit log entry (sistema unificado)
      await addAuditEntry({
        collection,
        documentId: oid(arrId),
        originalData: originalARRData.value,
        updatedData: payload,
      })

      showSuccessMessage('ARR actualizado correctamente')
      router.push({ name: 'privacy-residual-risk-list' })
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al actualizar ARR')
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    update,
    saveDraft,
    show,
    arrData,
    arrId,
    isSubmitting,
  }
}
